import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Add as AddIcon, Delete as DeleteIcon } from '@material-ui/icons';
import TriggerFieldSelect from './TriggerFieldSelect.component';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  conditionRow: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'flex-start',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
  },
  fieldControl: {
    minWidth: 200,
  },
  addButton: {
    marginTop: theme.spacing(1),
  },
  infoText: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
}));

// Distinct tables present in the field catalog the API returned for the rule's service and level,
// in API order (the File table first). The API already applies the service/level filter; this only
// regroups what it returned so the Table and Field selects can cascade.
export const getTriggerTables = (validTriggerFields) => {
  const tables = [];
  validTriggerFields.forEach((field) => {
    if (!tables.some(t => t.tableName === field.tableName)) {
      tables.push({
        tableName: field.tableName,
        tableDisplayName: field.tableDisplayName,
        isFileTable: !!field.isFileTable,
        isSegmentTable: !!field.isSegmentTable,
      });
    }
  });
  return tables;
};

// The table a condition row is on: the explicit Table selection (UI-only, never sent to the API),
// or the table of its chosen field for conditions loaded from an existing rule.
export const getConditionTableName = (condition, validTriggerFields) => {
  if (condition.tableName) {
    return condition.tableName;
  }
  const field = validTriggerFields.find(f => f.validTriggerFieldId === condition.validTriggerFieldId);
  return field ? field.tableName : null;
};

// Segment-level rules must place every non-File condition on a single segment table.
// Returns the tableName of the first condition on a non-File table, or null.
export const getLockedTableName = (conditions, validTriggerFields) => {
  const tables = getTriggerTables(validTriggerFields);
  for (const condition of conditions) {
    const tableName = getConditionTableName(condition, validTriggerFields);
    const table = tables.find(t => t.tableName === tableName);
    if (table && !table.isFileTable) {
      return table.tableName;
    }
  }
  return null;
};

const ConditionBuilder = ({
  conditions,
  onChange,
  validTriggerFields,
  isLoadingFields,
  operators,
  isSegmentLevel,
  title,
  description,
}) => {
  const classes = useStyles();

  const isNullOperator = (operatorId) => {
    const operator = operators.find(o => o.id === operatorId);
    return ['IS NULL', 'IS NOT NULL'].includes(operator?.additionalField);
  };

  const handleAddCondition = () => {
    onChange([
      ...conditions,
      {
        tableName: null,
        validTriggerFieldId: null,
        triggerFieldName: '',
        comparisonOperatorId: null,
        comparisonValue: null,
        conditionOrder: conditions.length + 1,
      },
    ]);
  };

  const handleRemoveCondition = (index) => {
    const newConditions = conditions.filter((_, i) => i !== index);
    // Renumber conditionOrder
    onChange(newConditions.map((c, i) => ({ ...c, conditionOrder: i + 1 })));
  };

  const handleConditionChange = (index, changes) => {
    const newConditions = [...conditions];
    newConditions[index] = {
      ...newConditions[index],
      ...changes,
    };

    // IS NULL / IS NOT NULL take no comparison value
    if (isNullOperator(newConditions[index].comparisonOperatorId)) {
      newConditions[index].comparisonValue = null;
    }

    onChange(newConditions);
  };

  // Changing the table invalidates the field (and the value typed for it); the operator is kept
  const handleTableSelect = (index, tableName) => {
    handleConditionChange(index, {
      tableName: tableName || null,
      validTriggerFieldId: null,
      triggerFieldName: '',
      comparisonValue: null,
    });
  };

  const handleFieldSelect = (index, validTriggerFieldId, field) => {
    handleConditionChange(index, {
      validTriggerFieldId,
      triggerFieldName: field ? field.fieldName : '',
      tableName: field ? field.tableName : conditions[index].tableName,
    });
  };

  const tables = getTriggerTables(validTriggerFields);
  const lockedTable = isSegmentLevel ? getLockedTableName(conditions, validTriggerFields) : null;
  const selectableTables = lockedTable
    ? tables.filter(t => t.isFileTable || t.tableName === lockedTable)
    : tables;

  // Options for one row: the selectable tables, plus the row's own table if it is no longer selectable
  // (e.g. a rule saved before the lock existed) so the current value still renders.
  const getRowTables = (rowTable) => {
    if (rowTable && !selectableTables.some(t => t.tableName === rowTable)) {
      const own = tables.find(t => t.tableName === rowTable);
      return own ? [...selectableTables, own] : selectableTables;
    }
    return selectableTables;
  };

  return (
    <Paper className={classes.container}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" className={classes.infoText}>
        {description || (
          <>
            All conditions must be true (AND logic) for the rule to fire. Pick the table first, then the field.
            {isSegmentLevel && ' Segment-level rules evaluate one segment table; File fields may be combined with it.'}
          </>
        )}
      </Typography>

      {conditions.length === 0 && (
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
          No conditions added. Click "Add Condition" to start.
        </Typography>
      )}

      {conditions.map((condition, index) => {
        const rowTable = getConditionTableName(condition, validTriggerFields);
        const rowFields = rowTable ? validTriggerFields.filter(f => f.tableName === rowTable) : [];

        return (
          <Box key={index} className={classes.conditionRow}>
            <FormControl className={classes.fieldControl} required disabled={isLoadingFields}>
              <InputLabel>Table</InputLabel>
              <Select
                value={rowTable || ''}
                onChange={(e) => handleTableSelect(index, e.target.value)}
              >
                <MenuItem value="">
                  <em>Select table</em>
                </MenuItem>
                {getRowTables(rowTable).map((table) => (
                  <MenuItem key={table.tableName} value={table.tableName}>
                    {table.tableDisplayName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TriggerFieldSelect
              value={condition.validTriggerFieldId}
              onChange={(id, field) => handleFieldSelect(index, id, field)}
              fields={rowFields}
              isLoading={isLoadingFields}
              label="Field"
              required
              disabled={!rowTable}
            />

            <FormControl className={classes.fieldControl} required>
              <InputLabel>Operator</InputLabel>
              <Select
                value={condition.comparisonOperatorId || ''}
                onChange={(e) => handleConditionChange(index, { comparisonOperatorId: e.target.value || null })}
              >
                <MenuItem value="">
                  <em>Select operator</em>
                </MenuItem>
                {operators.map((op) => (
                  <MenuItem key={op.id} value={op.id}>
                    {op.title} ({op.additionalField})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Comparison Value"
              value={condition.comparisonValue || ''}
              onChange={(e) => handleConditionChange(index, { comparisonValue: e.target.value })}
              disabled={isNullOperator(condition.comparisonOperatorId)}
              placeholder={
                isNullOperator(condition.comparisonOperatorId) ? 'Not needed for NULL checks' : 'Enter value'
              }
              className={classes.fieldControl}
            />

            <IconButton onClick={() => handleRemoveCondition(index)} size="small" style={{ marginTop: 8 }}>
              <DeleteIcon />
            </IconButton>
          </Box>
        );
      })}

      <Button
        variant="outlined"
        color="primary"
        startIcon={<AddIcon />}
        onClick={handleAddCondition}
        className={classes.addButton}
        disabled={isLoadingFields}
      >
        Add Condition
      </Button>
    </Paper>
  );
};

ConditionBuilder.propTypes = {
  conditions: PropTypes.arrayOf(
    PropTypes.shape({
      tableName: PropTypes.string,
      validTriggerFieldId: PropTypes.number,
      triggerFieldName: PropTypes.string,
      comparisonOperatorId: PropTypes.string,
      comparisonValue: PropTypes.string,
      conditionOrder: PropTypes.number,
    }),
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  validTriggerFields: PropTypes.array,
  isLoadingFields: PropTypes.bool,
  operators: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      additionalField: PropTypes.string,
    }),
  ),
  isSegmentLevel: PropTypes.bool,
  title: PropTypes.string,
  description: PropTypes.string,
};

ConditionBuilder.defaultProps = {
  validTriggerFields: [],
  isLoadingFields: false,
  operators: [],
  isSegmentLevel: false,
  title: 'Trigger Conditions',
  description: null,
};

export default ConditionBuilder;
